import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { USE_API, bundledContent, fetchPublicContent } from './source';

const ContentContext = createContext(null);

const EMPTY = { projects: [], fineArts: [] };

// status: 'loading' | 'ready' | 'error'
export function ContentProvider({ children }) {
  const [state, setState] = useState(() =>
    USE_API ? { status: 'loading', content: EMPTY } : { status: 'ready', content: bundledContent },
  );
  const latest = useRef(0);
  const { pathname } = useLocation();
  const inAdmin = pathname.startsWith('/admin');

  const reload = useCallback(async () => {
    if (!USE_API) return;
    const request = (latest.current += 1);
    try {
      const content = await fetchPublicContent();
      if (request === latest.current) setState({ status: 'ready', content });
    } catch {
      // What was loaded before may no longer be published: drop it.
      if (request === latest.current) setState({ status: 'error', content: EMPTY });
    }
  }, []);

  // Ask again on every navigation within the site...
  useEffect(() => {
    if (!inAdmin) reload();
  }, [pathname, inAdmin, reload]);

  // ...and when the visitor comes back to the tab.
  useEffect(() => {
    if (!USE_API) return undefined;
    const onVisible = () => {
      if (document.visibilityState === 'visible' && !window.location.pathname.startsWith('/admin')) reload();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [reload]);

  return (
    <ContentContext.Provider value={{ status: state.status, ...state.content, reload }}>
      {children}
    </ContentContext.Provider>
  );
}

export function useContent() {
  return useContext(ContentContext);
}
